import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle,
  MessageSquare, HelpCircle, FileText, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'support@easymyvisa.com',
      subtext: 'We respond within 24 hours'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+91 12345 67890',
      subtext: 'Mon-Sat, 9AM-6PM IST'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Mumbai, Maharashtra',
      subtext: 'India'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Monday - Saturday',
      subtext: '9:00 AM - 6:00 PM IST'
    }
  ];

  const faqs = [
    {
      question: 'How long does visa processing take?',
      answer: 'Processing times vary by country, typically ranging from 3-15 business days.'
    },
    {
      question: 'What documents do I need?',
      answer: 'Required documents vary by visa type. Common requirements include passport, photos, and financial proof.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we use industry-standard encryption to protect all your personal information.'
    },
    {
      question: 'Can I track my application?',
      answer: 'Absolutely! You can track your application status in real-time through your dashboard.'
    }
  ];

  const supportCategories = [
    { icon: FileText, title: 'Application Help', desc: 'Get help with your visa application' },
    { icon: HelpCircle, title: 'General Inquiry', desc: 'Questions about our services' },
    { icon: Globe, title: 'Visa Requirements', desc: 'Learn about visa requirements' },
    { icon: MessageSquare, title: 'Feedback', desc: 'Share your experience with us' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl p-12 border border-[#D1CBBF]">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Message Sent!
              </h1>
              <p className="text-[#4A5568] mb-8">
                Thank you for reaching out. Our team will review your message and get back to you within 24 hours.
              </p>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                }}
                className="bg-[#FF5A36] hover:bg-[#E0492B]"
              >
                Send Another Message
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar />
      <SEO
        title="Contact Us"
        description="Get in touch with EasyMyVisa for visa application support. Our experts are available to help you with your visa queries."
        keywords="contact EasyMyVisa, visa support, visa help, visa application help"
        canonical="/contact"
      />
      
      <main className="pt-24 pb-16" data-testid="contact-page">
        {/* Hero Section */}
        <div className="bg-[#0A1E40] py-16 mb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-[#FF5A36] text-sm font-bold tracking-[0.2em] uppercase">
              Get In Touch
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              We're Here To Help
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto">
              Have questions about your visa application? Our dedicated support team is ready to assist you every step of the way.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 -mt-24">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-[#D1CBBF] card-hover text-center">
                <div className="w-12 h-12 bg-[#FF5A36]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-6 h-6 text-[#FF5A36]" />
                </div>
                <h3 className="font-semibold text-[#0A1E40] mb-1">{info.title}</h3>
                <p className="text-[#0A1E40] font-medium">{info.details}</p>
                <p className="text-sm text-[#4A5568]">{info.subtext}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-2xl p-8 border border-[#D1CBBF]">
                <h2 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1.5"
                        placeholder="Your name"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-1.5"
                        placeholder="your@email.com"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-1.5"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="mt-1.5"
                        placeholder="How can we help?"
                        required
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-1.5"
                      placeholder="Tell us more about your inquiry..."
                      rows={5}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-[#FF5A36] hover:bg-[#E0492B] py-6 rounded-full"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Support Categories & FAQs */}
            <div className="space-y-8">
              {/* Support Categories */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  How Can We Help?
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {supportCategories.map((category, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 border border-[#D1CBBF] card-hover cursor-pointer">
                      <category.icon className="w-8 h-8 text-[#FF5A36] mb-3" />
                      <h3 className="font-semibold text-[#0A1E40] mb-1">{category.title}</h3>
                      <p className="text-sm text-[#4A5568]">{category.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-xl p-5 border border-[#D1CBBF]">
                      <h3 className="font-semibold text-[#0A1E40] mb-2">{faq.question}</h3>
                      <p className="text-[#4A5568] text-sm">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
